import { gql, useQuery } from '@apollo/client';
import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import type {
  GetEmbedContentQuery,
  GetEmbedContentQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import PostPreviewQuillComponent from '../../post/PostPreviewQuillComponent';
import EDITOR_INTEGRATION_MENTIONS from '../mentions/EditorIntegrationMentions';
import styles from './EmbedComponent.module.scss';
import EmbedFallback from './EmbedFallback';
import IframeWithFallback from './IframeWithFallback';
import type { EmbedValue, PreviewMode } from './matchEmbed';
import { EmbedMode, embedUrl } from './matchEmbed';

type RenderMode = 'image' | 'content' | 'embed';

export const getEmbedPresentationOptions = (type: string) => {
  const renderModes: {
    [key: string]: RenderMode;
  } = {
    gliffy: 'image',
    'quip-thread': 'content',
    'zendesk-article': 'content',
  };

  return {
    allowXSmallHeight: ['google-drive-folder', 'twitter'].includes(type),
    renderMode: renderModes[type] || 'embed',
  };
};

const GET_EMBED_QUERY = gql`
  query getEmbedContent($type: MentionType!, $id: ID!) {
    mention(type: $type, id: $id) {
      id
      preview {
        content
      }
    }
  }
`;

interface EmbedComponentScaleContainerProps {
  children: React.ReactNode;
}

export const EmbedComponentScaleContainer = ({
  children,
}: EmbedComponentScaleContainerProps) => {
  return (
    <div className={styles.scaleContainer}>
      <div className={styles.scaledContent}>{children}</div>
    </div>
  );
};

interface EmbedComponentProps extends EmbedValue {
  readOnly: boolean;
  className?: string;
  previewMode?: PreviewMode;
}

const EmbedComponent = ({
  id,
  type,
  params,
  readOnly,
  className,
  previewMode,
}: EmbedComponentProps) => {
  const { renderMode } = getEmbedPresentationOptions(type);

  const { apiEnum } = EDITOR_INTEGRATION_MENTIONS[type] || {};
  const { data, loading, error, refetch } = useQuery<
    GetEmbedContentQuery,
    GetEmbedContentQueryVariables
  >(GET_EMBED_QUERY, {
    // @ts-expect-error
    variables: { id, type: apiEnum },
    skip: !apiEnum || renderMode !== 'content',
  });

  const src = embedUrl(
    { id, type, params },
    readOnly ? EmbedMode.READONLY : EmbedMode.EDIT,
    previewMode,
  );
  if (!src) return null;

  const title = _.startCase(type);
  if (renderMode === 'image') {
    return <img src={src} alt={title} className={className} />;
  }

  if (renderMode === 'embed') {
    return (
      <IframeWithFallback
        allowFullScreen
        frameBorder="0"
        src={src}
        title={title}
        className={className}
        type={type}
        fallback={
          <div className={className}>
            <EmbedFallback
              id={id}
              type={type}
              error={error}
              refetch={refetch}
            />
          </div>
        }
      />
    );
  }

  const content = data?.mention?.preview?.content;

  return (
    <div className={classNames(styles.container, className)}>
      {!loading && (
        <>
          {content ? (
            <EmbedComponentScaleContainer>
              <PostPreviewQuillComponent content={content} />
            </EmbedComponentScaleContainer>
          ) : (
            <EmbedFallback
              id={id}
              type={type}
              refetch={refetch}
              error={error}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EmbedComponent;
